import { useMemo, useState } from 'react';
import { fmtMemberId } from '@lib/member-id';
import { SORT_FIELDS, SORT_LABELS, sortQueue, type SortDir, type SortField } from '@lib/queue-sort';
import type { QueueItem } from '@/types/intake';

/**
 * "Awaiting resulting: N" tile. Tapping it reveals the list; tapping a row loads it for confirm.
 * The list shows the three operative numbers per kit (USPS return tracking, kit barcode, member id)
 * and the USPS delivered-to-lab date, and can be re-sequenced (see queue-sort) by any of those keys,
 * by last name, or by delivery date, ascending or descending. Sorting is client-side: the queue
 * arrives in one payload, so re-sequencing never re-hits the API.
 */

/** USPS delivered-to-lab calendar date (YYYY-MM-DD); blank when absent. No timezone shift. */
function fmtRecvDate(iso: string | null): string {
  return iso ? iso.slice(0, 10) : '';
}

export function QueueTile({
  count,
  items,
  loading,
  onSelect,
}: {
  count: number;
  items: QueueItem[];
  loading: boolean;
  onSelect: (item: QueueItem) => void;
}) {
  const [open, setOpen] = useState(false);
  const [field, setField] = useState<SortField>('delivery');
  const [dir, setDir] = useState<SortDir>('asc');

  const sorted = useMemo(() => sortQueue(items, field, dir), [items, field, dir]);

  return (
    <div className="mb-4">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="stat accent w-full text-left"
        style={{ cursor: 'pointer' }}
      >
        <div className="label">Awaiting resulting</div>
        <div className="value tab-num">{loading ? '...' : count}</div>
        <div className="sub">{open ? 'Hide list' : 'Tap to view list'}</div>
      </button>

      {open && (
        <>
          <div className="mt-3 flex items-center gap-2 px-1">
            <label htmlFor="queue-sort" className="field-label" style={{ marginBottom: 0 }}>
              Sort
            </label>
            <select
              id="queue-sort"
              value={field}
              onChange={(e) => setField(e.target.value as SortField)}
              className="select-input"
              style={{ width: 'auto', padding: '5px 8px', fontSize: 12 }}
            >
              {SORT_FIELDS.map((f) => (
                <option key={f} value={f}>
                  {SORT_LABELS[f]}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => setDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
              className="btn btn-sm"
              aria-label={dir === 'asc' ? 'Ascending' : 'Descending'}
            >
              {dir === 'asc' ? 'Asc ↑' : 'Desc ↓'}
            </button>
          </div>

          <ul className="li-list mt-3">
            {sorted.length === 0 && (
              <li className="px-3 py-3 text-sm" style={{ color: 'var(--ink-400)' }}>
                No kits awaiting resulting.
              </li>
            )}
            {sorted.map((it) => (
              <li key={it.kitBarcode}>
                <button type="button" onClick={() => onSelect(it)} className="li-list-row" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                  <div className="flex items-start justify-between gap-2">
                    <span className="font-semibold text-cdx-fg-strong">{it.memberName || '(no name)'}</span>
                    <span className="shrink-0 tab-num text-xs" style={{ color: 'var(--ink-400)' }}>
                      {fmtRecvDate(it.usps.sampleReceivedTs)}
                    </span>
                  </div>
                  <dl className="mt-1 grid grid-cols-[4.5rem,1fr] gap-x-2 text-xs leading-5">
                    <dt style={{ color: 'var(--ink-400)' }}>Tracking</dt>
                    <dd className="li-mono truncate">{it.usps.returnTracking || '-'}</dd>
                    <dt style={{ color: 'var(--ink-400)' }}>Barcode</dt>
                    <dd className="li-mono">{it.kitBarcode}</dd>
                    <dt style={{ color: 'var(--ink-400)' }}>Member</dt>
                    <dd className="li-mono">{fmtMemberId(it.memberId)}</dd>
                  </dl>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
